<template>
    <div
        class="deployment"
        :class="{'unavailable-for-drop': unavailableForDrop}"
        :style="itemStyle"
        @drop.stop="handleDrop"
        @dragover.prevent="handleDragOver"
        @contextmenu.prevent="editDeployment"
    >
        <DeploymentSpecificationUnavailable
            v-for="(specification, index) of unavailableSpecifications"
            :key="'notWorking|' + index"
            :specification="specification"
            :beginOfDay="beginOfDay"
            :beginDateTimeDeployment="beginDateTime"
            :endDateTimeDeployment="endDateTime"
        />
        <div class="shift-label" v-if="workScheduleAssignments.length === 0">{{ shiftLabel }}</div>
        <DeploymentWorkScheduleAssignment
            v-else
            v-for="workScheduleAssignment in workScheduleAssignments"
            :key="workScheduleAssignment.id"
            :deploymentId="deployment.id!"
            :beginDateTimeDeployment="beginDateTime"
            :endDateTimeDeployment="endDateTime"
            :workScheduleAssignment="workScheduleAssignment"
            :workSchedulePosition="workSchedulePositions[workScheduleAssignment.workSchedulePositionId]"
            :groupLabels="groupLabels"
            @dragStart:workScheduleAssignment="$emit('dragStart:workScheduleAssignment', $event)"
            @dragEnd:workScheduleAssignment="$emit('dragEnd:workScheduleAssignment', $event)"
        />
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CSSProperties} from 'vue';
    import {ApiClient, Deployment, OrganisationDeploymentState, Shift, WorkScheduleAssignment, DeploymentSpecification, DeploymentStateSpecification, Company} from '@cquential/api';
    import {PlanbordWorkSchedulePosition, ResourceWithKeyedByDateObjects, PlanbordWorkScheduleGroupLabel} from '@/types/api-object-extensions';
    import WorkScheduleUtil from '@/util/WorkScheduleUtil';
    import Loader from '@/components/modals/Loader.vue';
    import {WorkScheduleGridPosition, DateRange} from '@/types/types';
    import {WorkSchedulePositionDragData, WorkScheduleAssignmentDragData} from '@/types';
    import DeploymentWorkScheduleAssignment from '@/components/WorkSchedule/DeploymentWorkScheduleAssignment.vue';
    import DeploymentSpecificationUnavailable from '@/components/WorkSchedule/DeploymentSpecificationUnavailable.vue';
    import ShiftStateUpdateModal from '@/components/modals/ShiftStateUpdateModal.vue';
    import Dates from '@cquential/date-utils';

    @Component({
        components: {
            DeploymentWorkScheduleAssignment,
            DeploymentSpecificationUnavailable,
        },
        emits: ['dragStart:workScheduleAssignment', 'dragEnd:workScheduleAssignment', 'reload:workSchedulePosition'],
    })
    export default class DeploymentRow extends Vue {
        @Prop({required: true}) public renderedDateRange!: DateRange;
        @Prop({required: true}) public deployment!: Deployment;
        @Prop({required: true}) public groupLabels!: Record<number, PlanbordWorkScheduleGroupLabel>;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop() public resource?: ResourceWithKeyedByDateObjects;
        @Prop() public company?: Company;
        @Prop() public shift?: Shift;
        @Prop({default: []}) public timeBlocksWithoutWorkScheduleAssignments!: DateRange[];
        @Prop({type: Boolean}) public unavailableForDrop!: boolean;
        @Prop({default: []}) public specificationsNotAvailableForDrop!: number[];
        @Prop({default: []}) public workScheduleAssignments!: WorkScheduleAssignment[];
        @Prop({required: true}) public workSchedulePositions!: Record<number, PlanbordWorkSchedulePosition>;
        @Prop({required: true}) public workingDeploymentStates!: DeploymentStateSpecification[];
        @Prop({required: true}) public visibleDeploymentSpecificationTypeIds!: number[];
        @Prop({required: true}) public rowIndex!: number;

        public get beginOfDay(): Date {
            return Dates.Utils.dateToAmsterdamMidnightDate(this.deployment.date);
        }

        public get beginDateTime(): Date {
            return this.deployment.specifications![0]!.begin;
        }

        public get endDateTime(): Date {
            return this.deployment.specifications![this.deployment.specifications!.length - 1]!.end;
        }

        public get itemStyle(): CSSProperties {
            const gridPosition: WorkScheduleGridPosition = WorkScheduleUtil.getGridPositionInHours(this.beginDateTime, this.endDateTime, this.renderedDateRange);

            return {
                'grid-row': this.rowIndex + 1,
                '--offset-left': 'calc((var(--left-space-holder-hours) + ' + gridPosition.left + ') * var(--hour-cell-width))',
                '--width': 'calc(' + gridPosition.width + ' * var(--hour-cell-width))',
            };
        }

        public get shiftLabel(): string {
            if (this.organisationDeploymentStates[this.deployment.stateId] && this.organisationDeploymentStates[this.deployment.stateId].code !== null) {
                return this.organisationDeploymentStates[this.deployment.stateId].code!;
            }

            return this.shift?.template!.code ?? '';
        }

        public get availableDeploymentStateSpecificationIds(): number[] {
            return this.workingDeploymentStates.map((state: DeploymentStateSpecification): number => state.deploymentStateId);
        }

        public get unavailableSpecifications(): DeploymentSpecification[] {
            return this.deployment.specifications!.filter((specification: DeploymentSpecification): boolean => {
                if (specification.end < this.renderedDateRange.begin! || specification.begin > this.renderedDateRange.end!) {
                    return false;
                }

                return this.specificationsNotAvailableForDrop.includes(specification.id!)
                    || !this.availableDeploymentStateSpecificationIds.includes(specification.stateId)
                    || !this.visibleDeploymentSpecificationTypeIds.includes(specification.typeId)
                    || !this.organisationDeploymentStates[specification.stateId]?.properties.fulfillsRequiredShift;
            });
        }

        public handleDragOver(event: DragEvent): void {
            if (this.unavailableForDrop) {
                event.preventDefault();
                event.dataTransfer!.dropEffect = 'none';
            }
        }

        public handleDrop(event: DragEvent): void {
            if (!event.dataTransfer || this.unavailableForDrop) {
                event.preventDefault();

                return;
            }

            let parsedData: WorkSchedulePositionDragData | WorkScheduleAssignmentDragData;
            try {
                parsedData = JSON.parse(event.dataTransfer.getData('text'));
            } catch {
                event.preventDefault();

                return;
            }

            if ('workSchedulePositionId' in parsedData) {
                this.createNewAssignmentsFromDroppedDragData(parsedData);
            } else {
                this.updateAndCreateNewAssignmentsFromDroppedDragData(parsedData);
            }
        }

        private createNewAssignmentsFromDroppedDragData(data: WorkSchedulePositionDragData): void {
            const workSchedulePosition: PlanbordWorkSchedulePosition | undefined = this.workSchedulePositions[data.workSchedulePositionId];
            if (!workSchedulePosition) {
                return;
            }
            const newAssignments: WorkScheduleAssignment[] = [];

            for (const timeBlocksWithoutWorkScheduleAssignment of workSchedulePosition.timeBlocksWithoutWorkScheduleAssignment) {
                newAssignments.push(...this.createNewWorkScheduleAssignmentsBasedOnTimeBlocksWithoutWorkScheduleAssignment(data.workSchedulePositionId, timeBlocksWithoutWorkScheduleAssignment.begin, timeBlocksWithoutWorkScheduleAssignment.end));
            }

            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('workSchedule');
            ApiClient.instance.saveWorkScheduleAssignments(newAssignments).then((): void => {
                // Further loading of workSchedule is done in WorkSchedule.vue
                this.$emit('reload:workSchedulePosition', workSchedulePosition.id!);
            }).catch((): void => {
                /** @see Loader.setError() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setError('workSchedule');
            });
        }

        private updateAndCreateNewAssignmentsFromDroppedDragData(data: WorkScheduleAssignmentDragData): void {
            let workScheduleAssignment: WorkScheduleAssignment | null = null;
            for (const workSchedulePosition of Object.values(this.workSchedulePositions)) {
                workScheduleAssignment = workSchedulePosition.workScheduleAssignments?.find((assignment: WorkScheduleAssignment): boolean => assignment.id === data.workScheduleAssignmentId) ?? null;
                if (workScheduleAssignment !== null) {
                    break;
                }
            }

            if (!workScheduleAssignment) {
                return;
            }

            const currentAndNewAssignments: WorkScheduleAssignment[] = this.createNewWorkScheduleAssignmentsBasedOnTimeBlocksWithoutWorkScheduleAssignment(workScheduleAssignment.workSchedulePositionId, workScheduleAssignment.beginDateTime, workScheduleAssignment.endDateTime);
            // We want to update the currently dragged assignment, so assign the id to the first entry and allow potential other entries to be created
            currentAndNewAssignments[0].id = workScheduleAssignment.id!;

            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('workSchedule');
            ApiClient.instance.saveWorkScheduleAssignments(currentAndNewAssignments).then((): void => {
                this.$emit('reload:workSchedulePosition', workScheduleAssignment.workSchedulePositionId!);
            }).catch((): void => {
                /** @see Loader.setError() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setError('workSchedule');
            });
        }

        private createNewWorkScheduleAssignmentsBasedOnTimeBlocksWithoutWorkScheduleAssignment(
            workSchedulePositionId: number,
            workScheduleAssignmentBeginDateTime: Date,
            workScheduleAssignmentEndDateTime: Date,
        ): WorkScheduleAssignment[] {
            const assignments: WorkScheduleAssignment[] = [];
            for (const deploymentTimeRange of this.timeBlocksWithoutWorkScheduleAssignments) {
                if (WorkScheduleUtil.isDateRangeOverlapping(deploymentTimeRange, workScheduleAssignmentBeginDateTime, workScheduleAssignmentEndDateTime)) {
                    assignments.push({
                        workSchedulePositionId: workSchedulePositionId,
                        deploymentId: this.deployment.id!,
                        beginDateTime: Dates.Utils.max(deploymentTimeRange.begin, workScheduleAssignmentBeginDateTime),
                        endDateTime: Dates.Utils.min(deploymentTimeRange.end, workScheduleAssignmentEndDateTime),
                    });
                }
            }

            return assignments;
        }

        public editDeployment(): void {
            if (!this.deployment.id) {
                return;
            }

            /** @see ShiftStateUpdateModal.openModal() */
            // @ts-expect-error TS2339
            ShiftStateUpdateModal.__vfdConstructor.openModal(this.resource, this.company, this.deployment, new Date(this.deployment.date));
        }
    }
</script>

<style lang="scss" scoped>
    .deployment {
        position: relative;
        left: var(--offset-left);
        width: var(--width);
        border-top: 1px solid var(--deployment-marker-border-top-color);
        box-shadow: var(--deployment-marker-default-shadow);
        background: var(--deployment-marker-default-background);
        transition: var(--theme-generic-transition);
        margin: var(--cell-padding) 0;
        min-height: 1.3rem;
        max-height: 1.75rem;

        &:hover {
            box-shadow: var(--deployment-marker-hover-shadow);
        }

        .shift-label {
            color: var(--work-schedule-shift-label-color);
            padding: var(--deployment-holder-padding);
            font-style: italic;
        }

        &.unavailable-for-drop {
            opacity: 0.4;
        }
    }
</style>
